import { useState, useEffect } from 'react';
import { FileText, Users, Mail, Settings, ArrowRight, RefreshCw, Image } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const AdminHome = () => {
  const [stats, setStats] = useState({
    sections: 0,
    members: 0,
    messages: 0,
    unreadMessages: 0,
    images: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const [contentRes, membersRes, messagesRes, imagesRes] = await Promise.all([
        adminApi.get(`/content`),
        adminApi.get(`/members?visible_only=false`),
        adminApi.get(`/contact/submissions`),
        adminApi.get(`/images/list`).catch(() => ({ data: [] }))
      ]);

      setStats({
        sections: contentRes.data.length,
        members: membersRes.data.length,
        messages: messagesRes.data.length,
        unreadMessages: messagesRes.data.filter(m => !m.is_read).length,
        images: imagesRes.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const cards = [
    {
      title: 'Contenus',
      value: stats.sections,
      subtitle: 'sections éditables',
      icon: FileText,
      color: 'bg-blue-500',
      link: '/admin/content'
    },
    {
      title: 'Images',
      value: stats.images,
      subtitle: 'images téléversées',
      icon: Image,
      color: 'bg-indigo-500',
      link: '/admin/images'
    },
    {
      title: 'Membres',
      value: stats.members,
      subtitle: 'membres enregistrés',
      icon: Users,
      color: 'bg-green-500',
      link: '/admin/members'
    },
    {
      title: 'Messages',
      value: stats.messages,
      subtitle: `${stats.unreadMessages} non lu(s)`,
      icon: Mail,
      color: 'bg-amber-500',
      link: '/admin/messages'
    },
    {
      title: 'Paramètres',
      value: '-',
      subtitle: 'Configuration du site',
      icon: Settings,
      color: 'bg-purple-500',
      link: '/admin/settings'
    }
  ];

  return (
    <div className="space-y-8" data-testid="admin-home">
      {/* Welcome */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h2 className="font-playfair text-2xl font-semibold text-[#0F172A] mb-2">
          Bienvenue dans l'administration
        </h2>
        <p className="text-slate-600 font-inter">
          Gérez les contenus, les membres et les paramètres du site Place Financière BFC.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, idx) => (
          <Link key={idx} to={card.link}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid={`admin-card-${card.title.toLowerCase()}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-inter font-medium text-slate-600">
                  {card.title}
                </CardTitle>
                <div className={`w-10 h-10 ${card.color} rounded-lg flex items-center justify-center`}>
                  <card.icon className="w-5 h-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-playfair font-bold text-[#0F172A]">
                  {loading ? '...' : card.value}
                </div>
                <p className="text-sm text-slate-500 font-inter mt-1">
                  {card.subtitle}
                </p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="font-playfair text-lg font-semibold text-[#0F172A] mb-4">
          Actions rapides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/admin/content">
            <Button variant="outline" className="w-full justify-between h-auto py-4" data-testid="quick-action-content">
              <span className="flex items-center gap-2">
                <FileText size={18} />
                Modifier les contenus
              </span>
              <ArrowRight size={18} />
            </Button>
          </Link>
          <Link to="/admin/members">
            <Button variant="outline" className="w-full justify-between h-auto py-4" data-testid="quick-action-members">
              <span className="flex items-center gap-2">
                <Users size={18} />
                Gérer les membres
              </span>
              <ArrowRight size={18} />
            </Button>
          </Link>
          <Link to="/admin/messages">
            <Button variant="outline" className="w-full justify-between h-auto py-4" data-testid="quick-action-messages">
              <span className="flex items-center gap-2">
                <Mail size={18} />
                Voir les messages
                {stats.unreadMessages > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {stats.unreadMessages}
                  </span>
                )}
              </span>
              <ArrowRight size={18} />
            </Button>
          </Link>
        </div>
      </div>

      {/* Help */}
      <div className="bg-slate-50 rounded-lg p-6">
        <h3 className="font-playfair text-lg font-semibold text-[#0F172A] mb-2">
          Aide
        </h3>
        <p className="text-slate-600 font-inter text-sm mb-4">
          Depuis ce panneau d'administration, vous pouvez :
        </p>
        <ul className="space-y-2 text-sm text-slate-600 font-inter">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2" />
            <span><strong>Contenus</strong> : Modifier tous les textes, titres et descriptions des sections du site</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2" />
            <span><strong>Membres</strong> : Ajouter, modifier ou supprimer les membres affichés sur le site</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2" />
            <span><strong>Messages</strong> : Consulter les messages reçus via le formulaire de contact</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2" />
            <span><strong>Paramètres</strong> : Configurer les emails destinataires et les informations de contact</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdminHome;
